import React, { useEffect, useMemo, useContext, useState, Fragment } from "react";
import moment from "moment";
import cn from "classnames";
import { SettingsContext } from "../SettingsProvider";
import { itemsToBoardState, getEvents, TimelineEvent, BoardState } from "../tools/events";
import { TreeFunnel, TreeFunnelNode, treeFunnel, TreeFunnelInfo, FunnelItem } from "../tools/treeFunnel";
import { Board } from "../types";
import TreeCanvas from "./TreeCanvas";
import { loadSet, saveSet } from "../tools/storage";
import Statuses from "../common/Statuses";
import Tooltip, { Direction } from "../common/Tooltip";
import ItemsInNode from "./ItemsInNode";
import AttentionBox from "../common/AttentionBox";

import styles from "./TreeView.module.scss";
import IconInfo from "../common/IconInfo";
import Icon from "../common/Icon";

interface Props {
    board: Board;
    initialTrackedLabels?: Set<string>;
}

const TreeView: React.FC<Props> = function ({ board, initialTrackedLabels }: Props) {
    const { settings, context, labels } = useContext(SettingsContext);
    const { column, scaleBasedOnCount, mergeStatuses, firstStatusAsWorkflowStart, lastStatusAsWorkflowEnd } = settings;

    const [funnel, setFunnel] = useState<TreeFunnel>();
    const [funnelInfo, setFunnelInfo] = useState<TreeFunnelInfo>();
    const [events, setEvents] = useState<TimelineEvent[]>();
    const [boardState, setBoardState] = useState<BoardState>();
    const storeKey = useMemo(() => `trackedStatuses-${context.instanceId}-${column}`, [context.instanceId, column]);
    const [trackedLabels, setTrackedLabels] = useState<Set<string>>();
    const [firstLabel, setFirstLabel] = useState<string>();
    const [lastLabel, setLastLabel] = useState<string>();

    const [selectedNode, setSelectedNode] = useState<TreeFunnelNode>();
    const [filterItemsOut, setFilterItemsOut] = useState<Set<string>>(new Set());

    // load async setting from storage
    useEffect(() => {
        const f = async (): Promise<void> => {
            setTrackedLabels((await loadSet(storeKey)) || initialTrackedLabels || new Set([]));
        };
        f();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (board && column) {
            const now = moment();
            const currentBoard = itemsToBoardState(board, now, labels);
            const { events } = getEvents(currentBoard, board.activity_logs, column, "status");
            setEvents(events);
            setBoardState(currentBoard);
        }
    }, [board, column, labels]);

    useEffect(() => {
        if (events && boardState && column && trackedLabels) {
            let computedFirstLabel: string | undefined = undefined;
            let computedLastLabel: string | undefined = undefined;

            if (trackedLabels.size > 0) {
                const orderedSelectedIds = labels.orderedIds.filter((labelId) => trackedLabels.has(labelId));
                if (orderedSelectedIds.length > 0) {
                    if (firstStatusAsWorkflowStart) {
                        computedFirstLabel = orderedSelectedIds[0];
                    }
                    if (lastStatusAsWorkflowEnd) {
                        computedLastLabel = orderedSelectedIds[orderedSelectedIds.length - 1];
                    }
                }
            }

            const MAX_NODE_HEIGHT_RATIOS: { [setting: string]: number } = {
                "0": 1e10,
                "1": 40,
                "2": 20,
                "3": 10,
                "4": 1
            };

            const { funnel, info } = treeFunnel(events, column, labels, boardState, {
                centerVertically: true,
                trackedStatuses: trackedLabels,
                firstStatus: computedFirstLabel,
                lastStatus: computedLastLabel,
                mergeStatuses,
                scaleBasedOnCount,
                weightColumnId: settings.numberColumn,
                filterLevelsAbove: settings.filterLevelsAbove,
                filterNodeWeightLessThan: settings.filterNodeWeightLessThan,
                maxNodeHeightRatio: settings.nodeHeightRescale
                    ? MAX_NODE_HEIGHT_RATIOS[settings.nodeHeightRescale]
                    : MAX_NODE_HEIGHT_RATIOS[0],
                filterItemsOut
            });
            setFunnel(funnel);
            setFunnelInfo(info);
            setFirstLabel(computedFirstLabel);
            setLastLabel(computedLastLabel);
            saveSet(storeKey, trackedLabels);
        }
        // eslint-disable-next-line
    }, [
        events,
        boardState,
        trackedLabels,
        firstStatusAsWorkflowStart,
        lastStatusAsWorkflowEnd,
        mergeStatuses,
        scaleBasedOnCount,
        storeKey,
        settings.numberColumn,
        settings.filterLevelsAbove,
        settings.filterNodeWeightLessThan,
        settings.nodeHeightRescale,
        filterItemsOut
    ]);

    const handleSetSelectedLabelIds = (selectedLabelIds: Set<string>): void => {
        setTrackedLabels(selectedLabelIds);
        setSelectedNode(undefined);
    };

    const handleSelectAllLabelIds = (): void => {
        setTrackedLabels(new Set(labels.orderedIds));
        setSelectedNode(undefined);
    };

    const handleSelectNode = (node: TreeFunnelNode | null): void => {
        if (!node || selectedNode === node) {
            setSelectedNode(undefined);
        } else {
            setSelectedNode(node);
        }
    };

    //
    // Hiding items
    //
    const handleFilterItemsOut = (items: FunnelItem[]): void => {
        const newSet = new Set(filterItemsOut);
        items.forEach((item) => newSet.add(item.id));
        setFilterItemsOut(newSet);
        setSelectedNode(undefined);
    };

    const handleClearFilterItemsOut = (): void => {
        setFilterItemsOut(new Set());
    };

    const dark = context.theme === "dark";

    if (funnel && boardState && trackedLabels) {
        const hiddenLevelsCount = settings.filterLevelsAbove
            ? Math.max(0, funnel.length - settings.filterLevelsAbove)
            : 0;
        return (
            <div className={cn({ [styles.dark]: dark })}>
                <Statuses selectedLabelIds={trackedLabels} setSelectedLabelIds={handleSetSelectedLabelIds} />
                {trackedLabels.size === 0 && (
                    <AttentionBox title="Choose statuses" inContainer>
                        <p>
                            Choose one or more statuses above to see the Tree / Journey graph for them. (
                            <span className="link" onClick={handleSelectAllLabelIds}>
                                select all
                            </span>
                            )
                        </p>
                        <p>You can drag and drop stauses to change their order.</p>
                    </AttentionBox>
                )}
                {trackedLabels.size > 0 && (
                    <Fragment>
                        <div className={styles.filters}>
                            {settings.filterLevelsAbove !== undefined && (
                                <div className={cn(styles.filter, "tooltip")}>
                                    <Icon iconClass="icon__closed_eye" />
                                    &nbsp;&nbsp;Hiding <span className={styles.strong}>{hiddenLevelsCount}</span> level
                                    {hiddenLevelsCount > 1 ? "s" : ""} above level{" "}
                                    <span className={styles.strong}>{settings.filterLevelsAbove}</span>&nbsp;
                                    <IconInfo />
                                    <Tooltip
                                        type="dark"
                                        wide
                                        delay
                                        direction={Direction.BOTTOM}
                                        text={`See "Hide levels above" View Setting.`}
                                    />
                                </div>
                            )}
                            {settings.filterNodeWeightLessThan !== undefined && (
                                <div className={styles.filter}>
                                    <Icon iconClass="icon__closed_eye" />
                                    &nbsp;&nbsp;Hiding nodes with{" "}
                                    {settings.numberColumn !== undefined ? "weight" : "count"} less than{" "}
                                    <span className={styles.strong}>{settings.filterNodeWeightLessThan}</span>
                                </div>
                            )}
                            {funnelInfo?.rescaledNodeHeight && settings.scaleBasedOnCount && (
                                <div className={cn(styles.filter, "tooltip")}>
                                    <Icon iconClass="icon__showall" />
                                    &nbsp;&nbsp;Nodes may be rescaled and not show actual weight ratio&nbsp;
                                    <IconInfo />
                                    <Tooltip
                                        type="dark"
                                        wide
                                        delay
                                        direction={Direction.BOTTOM}
                                        text={`This is to prevent very high nodes. Higher weight/count will still show as a bigger node, but ratio is not accurate. See "Max height of node" View Setting.`}
                                    />
                                </div>
                            )}
                            {filterItemsOut.size > 0 && (
                                <div
                                    className={cn(styles.filter, styles.actionable)}
                                    onClick={handleClearFilterItemsOut}
                                >
                                    <Icon iconClass="icon__closed_eye" />
                                    &nbsp;&nbsp;Hiding <span className={styles.strong}>{filterItemsOut.size}</span> item
                                    {filterItemsOut.size > 1 ? "s" : ""}.{" "}
                                    <span className={styles.strong}>Click to unhide</span>.
                                </div>
                            )}
                        </div>
                        <TreeCanvas
                            funnel={funnel}
                            connectorYPadding={5}
                            connectorYDefault={30}
                            onSelectNode={handleSelectNode}
                            selectedNode={selectedNode}
                        />
                        <ItemsInNode
                            items={selectedNode ? selectedNode.items : undefined}
                            selectedLevel={selectedNode ? selectedNode.level : undefined}
                            board={board}
                            boardState={boardState}
                            trackedLabels={trackedLabels}
                            firstLabel={firstLabel}
                            lastLabel={lastLabel}
                            weightColumnId={settings.numberColumn}
                            onFilterItemsOut={handleFilterItemsOut}
                        />
                    </Fragment>
                )}
            </div>
        );
    } else {
        return <div>...</div>;
    }
};

export default TreeView;
